/**
 * MinistrareIA - Video Links Page
 * Manage dynamic video links with view-limit security.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Video, Plus, Search, Trash2, X, Copy, Check,
  Loader2, Eye, Edit3,
  Upload, AlertCircle, RotateCcw, Image,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import videoLinkService from '../../services/global/videoLinkService';

// ============================================================
// Status Badge
// ============================================================
const StatusBadge = ({ status, colors }) => {
  const config = {
    processing: { label: 'Processando', bg: '#f59e0b20', color: '#f59e0b' },
    ready: { label: 'Pronto', bg: '#22c55e20', color: '#22c55e' },
    failed: { label: 'Falhou', bg: '#ef444420', color: '#ef4444' },
  };
  const c = config[status] || config.processing;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};

// ============================================================
// Empty State
// ============================================================
const EmptyState = ({ colors, onCreateNew }) => (
  <div style={{
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: '16px', padding: '40px',
  }}>
    <div style={{
      width: '80px', height: '80px', borderRadius: '20px',
      backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Video size={36} color={colors.accentPrimary} />
    </div>
    <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
      Nenhum video link criado
    </h3>
    <p style={{ fontSize: '14px', color: colors.textSecondary, textAlign: 'center', maxWidth: '360px', margin: 0 }}>
      Crie links de video com limite de visualizacoes para proteger seu conteudo.
    </p>
    <button onClick={onCreateNew} style={{
      display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
      backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
      borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
    }}>
      <Plus size={18} /> Novo Video Link
    </button>
  </div>
);

// ============================================================
// Create Modal
// ============================================================
const CreateModal = ({ colors, onClose, onCreated }) => {
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [name, setName] = useState('');
  const [viewLimit, setViewLimit] = useState(3);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) return;
    setUploading(true);
    setError('');
    try {
      await videoLinkService.create(file, name.trim(), viewLimit, thumbnail, setProgress);
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar video link');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }} onClick={onClose}>
      <div style={{
        backgroundColor: colors.bgSecondary, borderRadius: '16px', padding: '24px',
        width: '100%', maxWidth: '480px', margin: '16px',
        border: `1px solid ${colors.borderColor}`,
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
            Novo Video Link
          </h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textSecondary, marginBottom: '6px' }}>
              Nome
            </label>
            <input
              type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Ex: Video de boas-vindas"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgTertiary,
                color: colors.textPrimary, fontSize: '14px', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textSecondary, marginBottom: '6px' }}>
              Limite de visualizacoes por usuario
            </label>
            <input
              type="number" value={viewLimit} onChange={e => setViewLimit(Math.max(1, parseInt(e.target.value) || 1))}
              min={1} max={1000}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgTertiary,
                color: colors.textPrimary, fontSize: '14px', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textSecondary, marginBottom: '6px' }}>
              Video
            </label>
            {!file ? (
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                padding: '24px', borderRadius: '8px', border: `2px dashed ${colors.borderColor}`,
                backgroundColor: colors.bgTertiary, cursor: 'pointer', textAlign: 'center',
              }}>
                <Upload size={24} color={colors.textSecondary} />
                <span style={{ fontSize: '13px', color: colors.textSecondary }}>
                  Clique para selecionar um video (MP4, WebM, MOV)
                </span>
                <input
                  type="file" accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/3gpp"
                  onChange={e => setFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </label>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                borderRadius: '8px', backgroundColor: colors.bgTertiary,
                border: `1px solid ${colors.borderColor}`,
              }}>
                <Video size={18} color={colors.accentPrimary} />
                <span style={{ flex: 1, fontSize: '13px', color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </span>
                <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
                <button onClick={() => setFile(null)} style={{ background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer', padding: '2px' }}>
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: colors.textSecondary, marginBottom: '6px' }}>
              Thumbnail (opcional)
            </label>
            {!thumbnail ? (
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                padding: '16px', borderRadius: '8px', border: `2px dashed ${colors.borderColor}`,
                backgroundColor: colors.bgTertiary, cursor: 'pointer', textAlign: 'center',
              }}>
                <Image size={20} color={colors.textSecondary} />
                <span style={{ fontSize: '12px', color: colors.textSecondary }}>
                  Clique para selecionar uma imagem de capa (JPG, PNG, WebP)
                </span>
                <input
                  type="file" accept="image/jpeg,image/png,image/webp"
                  onChange={e => {
                    const f = e.target.files[0];
                    if (f) {
                      setThumbnail(f);
                      setThumbnailPreview(URL.createObjectURL(f));
                    }
                  }}
                  style={{ display: 'none' }}
                />
              </label>
            ) : (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                borderRadius: '8px', backgroundColor: colors.bgTertiary,
                border: `1px solid ${colors.borderColor}`,
              }}>
                {thumbnailPreview && (
                  <img src={thumbnailPreview} alt="Thumbnail" style={{
                    width: '48px', height: '36px', objectFit: 'cover', borderRadius: '4px',
                  }} />
                )}
                <span style={{ flex: 1, fontSize: '13px', color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {thumbnail.name}
                </span>
                <button onClick={() => {
                  setThumbnail(null);
                  if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
                  setThumbnailPreview(null);
                }} style={{ background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer', padding: '2px' }}>
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {uploading && (
            <div style={{ width: '100%', backgroundColor: colors.bgTertiary, borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${progress}%`, height: '4px', backgroundColor: colors.accentPrimary,
                transition: 'width 0.3s ease',
              }} />
              <div style={{ textAlign: 'center', fontSize: '12px', color: colors.textSecondary, padding: '4px 0' }}>
                {progress < 100 ? `Enviando... ${progress}%` : 'Processando video...'}
              </div>
            </div>
          )}

          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', borderRadius: '8px', backgroundColor: '#ef444415', color: '#ef4444', fontSize: '13px' }}>
              <AlertCircle size={16} /> {error}
            </div>
          )}

          <button
            type="submit" disabled={!file || !name.trim() || uploading}
            style={{
              padding: '12px', borderRadius: '8px', border: 'none',
              backgroundColor: !file || !name.trim() || uploading ? colors.bgTertiary : colors.accentPrimary,
              color: !file || !name.trim() || uploading ? colors.textSecondary : 'white',
              fontSize: '14px', fontWeight: 600, cursor: !file || !name.trim() || uploading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            }}
          >
            {uploading ? <><Loader2 size={16} className="animate-spin" /> Enviando...</> : <><Plus size={16} /> Criar Video Link</>}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// Video Link Card
// ============================================================
const VideoLinkCard = ({ video, colors, isSelected, onSelect, onCopy, copiedId }) => (
  <div
    onClick={() => onSelect(video)}
    style={{
      padding: '14px 16px', cursor: 'pointer',
      backgroundColor: isSelected ? `${colors.accentPrimary}10` : 'transparent',
      borderBottom: `1px solid ${colors.borderColor}`,
      transition: 'background-color 0.1s',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '40px', height: '40px', borderRadius: '10px',
        backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Video size={20} color={colors.accentPrimary} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontSize: '14px', fontWeight: 500, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {video.name}
          </span>
          <StatusBadge status={video.status} colors={colors} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: colors.textSecondary }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Eye size={12} /> {video.total_views || 0} views
          </span>
          <span>Limite: {video.view_limit}x</span>
        </div>
      </div>
      {video.status === 'ready' && (
        <button
          onClick={(e) => { e.stopPropagation(); onCopy(video); }}
          style={{
            background: 'transparent', border: 'none',
            color: copiedId === video.id ? '#22c55e' : colors.textSecondary,
            cursor: 'pointer', padding: '6px',
          }}
          title="Copiar link"
        >
          {copiedId === video.id ? <Check size={16} /> : <Copy size={16} />}
        </button>
      )}
    </div>
  </div>
);

// ============================================================
// Detail Panel
// ============================================================
const DetailPanel = ({ video, colors, onDelete, onUpdate, onResetView, views, loadingViews }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(video.name);
  const [editLimit, setEditLimit] = useState(video.view_limit);
  const [saving, setSaving] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  useEffect(() => {
    setEditName(video.name);
    setEditLimit(video.view_limit);
    setEditing(false);
  }, [video.id, video.name, video.view_limit]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(video.id, { name: editName, view_limit: editLimit });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleResetView = async (viewId) => {
    if (!window.confirm('Tem certeza que deseja resetar as visualizacoes deste viewer?')) return;
    setResettingId(viewId);
    try {
      await onResetView(video.id, viewId);
    } finally {
      setResettingId(null);
    }
  };

  return (
    <div style={{ padding: '20px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
          Detalhes
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setEditing(!editing)} style={{
            padding: '6px 12px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
            backgroundColor: 'transparent', color: colors.textSecondary, fontSize: '12px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <Edit3 size={14} /> Editar
          </button>
          <button onClick={() => onDelete(video.id)} style={{
            padding: '6px 12px', borderRadius: '6px', border: '1px solid #ef444440',
            backgroundColor: 'transparent', color: '#ef4444', fontSize: '12px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <Trash2 size={14} /> Excluir
          </button>
        </div>
      </div>

      {/* Info Cards - responsive with flex wrap */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
        <div style={{ flex: '1 1 100px', minWidth: '100px', padding: '12px', borderRadius: '10px', backgroundColor: colors.bgTertiary }}>
          <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Status</div>
          <StatusBadge status={video.status} colors={colors} />
        </div>
        <div style={{ flex: '1 1 100px', minWidth: '100px', padding: '12px', borderRadius: '10px', backgroundColor: colors.bgTertiary }}>
          <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Views Total</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>{video.total_views || 0}</div>
        </div>
        <div style={{ flex: '1 1 100px', minWidth: '100px', padding: '12px', borderRadius: '10px', backgroundColor: colors.bgTertiary }}>
          <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Limite</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>{video.view_limit}x</div>
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '10px', backgroundColor: colors.bgTertiary }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Nome</label>
            <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px' }}>Limite de visualizacoes</label>
            <input type="number" value={editLimit} onChange={e => setEditLimit(Math.max(1, parseInt(e.target.value) || 1))} min={1}
              style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary, color: colors.textPrimary, fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '8px 16px', borderRadius: '6px', border: 'none',
            backgroundColor: colors.accentPrimary, color: 'white', fontSize: '13px',
            fontWeight: 500, cursor: 'pointer',
          }}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      )}

      {/* Views Table */}
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, marginBottom: '12px' }}>
          Visualizacoes ({views.length})
        </div>
        {loadingViews ? (
          <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : views.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary, fontSize: '13px' }}>
            Nenhuma visualizacao ainda
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {views.map((view) => (
              <div key={view.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 12px', borderRadius: '8px', backgroundColor: colors.bgTertiary,
                fontSize: '13px', flexWrap: 'wrap',
              }}>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <div style={{ color: colors.textPrimary, fontWeight: 500 }}>
                    {view.viewer_type === 'lead'
                      ? (view.lead_name || view.lead_phone
                        ? `${view.lead_name || 'Sem nome'}${view.lead_phone ? ` - ${view.lead_phone}` : ''}`
                        : `Lead: ${view.lead_id?.slice(0, 8)}...`)
                      : `Device: ${view.viewer_identifier?.slice(0, 8)}...`}
                  </div>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>
                    Primeira vez: {new Date(view.first_viewed_at).toLocaleString('pt-BR')}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    padding: '4px 10px', borderRadius: '12px', fontWeight: 600, fontSize: '12px',
                    backgroundColor: view.view_count >= video.view_limit ? '#ef444420' : '#22c55e20',
                    color: view.view_count >= video.view_limit ? '#ef4444' : '#22c55e',
                  }}>
                    {view.view_count}/{video.view_limit}
                  </div>
                  <button
                    onClick={() => handleResetView(view.id)}
                    disabled={resettingId === view.id}
                    title="Resetar visualizacoes"
                    style={{
                      padding: '4px 8px', borderRadius: '6px', border: `1px solid ${colors.borderColor}`,
                      backgroundColor: 'transparent', color: colors.textSecondary, fontSize: '11px',
                      cursor: resettingId === view.id ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: '4px',
                      opacity: resettingId === view.id ? 0.5 : 1,
                    }}
                  >
                    {resettingId === view.id
                      ? <Loader2 size={12} className="animate-spin" />
                      : <RotateCcw size={12} />}
                    Resetar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================
const VideoLinksPage = () => {
  const { colors } = useTheme();
  const [videoLinks, setVideoLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [views, setViews] = useState([]);
  const [loadingViews, setLoadingViews] = useState(false);

  const loadVideoLinks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await videoLinkService.list();
      setVideoLinks(res.data || []);
    } catch (err) {
      console.error('Error loading video links:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVideoLinks();
  }, [loadVideoLinks]);

  // Poll for processing status
  useEffect(() => {
    const processing = videoLinks.filter(v => v.status === 'processing');
    if (processing.length === 0) return;

    const interval = setInterval(() => {
      loadVideoLinks();
    }, 3000);

    return () => clearInterval(interval);
  }, [videoLinks, loadVideoLinks]);

  // Load views when selecting a video
  useEffect(() => {
    if (!selectedVideo) return;
    const loadViews = async () => {
      setLoadingViews(true);
      try {
        const res = await videoLinkService.getViews(selectedVideo.id);
        setViews(res.data || []);
      } catch (err) {
        console.error('Error loading views:', err);
      } finally {
        setLoadingViews(false);
      }
    };
    loadViews();
  }, [selectedVideo]);

  const handleCopy = (video) => {
    navigator.clipboard.writeText(video.public_url);
    setCopiedId(video.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Tem certeza que deseja excluir este video link?')) return;
    try {
      await videoLinkService.delete(videoId);
      setVideoLinks(prev => prev.filter(v => v.id !== videoId));
      if (selectedVideo?.id === videoId) setSelectedVideo(null);
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleUpdate = async (videoId, data) => {
    try {
      const res = await videoLinkService.update(videoId, data);
      const updated = res.data;
      setVideoLinks(prev => prev.map(v => v.id === videoId ? updated : v));
      if (selectedVideo?.id === videoId) setSelectedVideo(updated);
    } catch (err) {
      console.error('Error updating:', err);
    }
  };

  const handleResetView = async (videoId, viewId) => {
    try {
      await videoLinkService.resetView(videoId, viewId);
      // Reload views
      const res = await videoLinkService.getViews(videoId);
      setViews(res.data || []);
      // Reload video links to refresh total_views
      await loadVideoLinks();
    } catch (err) {
      console.error('Error resetting view:', err);
    }
  };

  const filtered = videoLinks.filter(v =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', height: '100vh', backgroundColor: colors.bgPrimary, overflow: 'hidden' }}>
      {/* Left Panel - List */}
      <div style={{
        width: selectedVideo ? '360px' : '100%', minWidth: '280px', flexShrink: 0,
        borderRight: selectedVideo ? `1px solid ${colors.borderColor}` : 'none',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '16px', borderBottom: `1px solid ${colors.borderColor}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: colors.textPrimary }}>
              Video Links
            </h2>
            <button onClick={() => setShowCreateModal(true)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
              backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
              borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
            }}>
              <Plus size={16} /> Novo
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: colors.bgTertiary, fontSize: '12px' }}>
              <span style={{ color: colors.textSecondary }}>Total: </span>
              <span style={{ fontWeight: 600, color: colors.textPrimary }}>{videoLinks.length}</span>
            </div>
            <div style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: colors.bgTertiary, fontSize: '12px' }}>
              <span style={{ color: colors.textSecondary }}>Views: </span>
              <span style={{ fontWeight: 600, color: colors.textPrimary }}>
                {videoLinks.reduce((sum, v) => sum + (v.total_views || 0), 0)}
              </span>
            </div>
          </div>

          {/* Search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', borderRadius: '8px',
            backgroundColor: colors.bgTertiary,
          }}>
            <Search size={16} color={colors.textSecondary} />
            <input
              type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar video links..."
              style={{
                flex: 1, border: 'none', background: 'transparent',
                color: colors.textPrimary, fontSize: '13px', outline: 'none',
              }}
            />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <Loader2 size={24} color={colors.textSecondary} className="animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState colors={colors} onCreateNew={() => setShowCreateModal(true)} />
          ) : (
            filtered.map(video => (
              <VideoLinkCard
                key={video.id}
                video={video}
                colors={colors}
                isSelected={selectedVideo?.id === video.id}
                onSelect={setSelectedVideo}
                onCopy={handleCopy}
                copiedId={copiedId}
              />
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Detail */}
      {selectedVideo && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <DetailPanel
            video={selectedVideo}
            colors={colors}
            onDelete={handleDelete}
            onUpdate={handleUpdate}
            onResetView={handleResetView}
            views={views}
            loadingViews={loadingViews}
          />
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateModal colors={colors} onClose={() => setShowCreateModal(false)} onCreated={loadVideoLinks} />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default VideoLinksPage;
